// The application form (docs/plans/lettings-viewings-applications.md "Forms" §2; docs/plans/lettings-one-form.md): one
// state shape shared by the website's /rent/:slug/apply (site styling), staff "Convert to application" (admin kit) and —
// in mode 'viewing' — every "Book a viewing", and the payload the API takes (applicant.schemas.ts applicationFormSchema /
// staffApplicationFormSchema / viewingRequestFormSchema). Money is typed in pounds and sent in pence.
//
//   mode 'apply'    the full application: every section, every consent (today's rules).
//   mode 'viewing'  fieldErrors' short rules (name, email, mobile, move-in, slots, privacy). Since 29 Oct 2026 every form
//                   that asks for an application — the website's and staff / agents' — holds a viewing request to the
//                   whole of mode 'apply' plus the slots, in four steps (lib/applicationSteps.ts siteFormErrors,
//                   useFormSteps; toPayload `complete`); staff / agents may instead save a phone call with the contact
//                   details only (applicationSteps contactOnlyErrors / contactOnlyPayload).
//
// Names (2 Nov 2026, docs/plans/viewing-first-onboarding.md): First name and Last name, two required fields; the payload
// sends aboutYou.firstName / lastName and the server derives aboutYou.fullName. An older form with only fullName is split
// on the last space (fromApplication / fromLead).

import { phoneOk, phoneError } from './phone';
import { findNationality, needsShareCode } from './nationalities';

/** Income has to be this many times the monthly rent (reservations.defaults.ts AFFORDABILITY_MULTIPLE). */
export const AFFORDABILITY_MULTIPLE = 30;
export const ADDRESS_HISTORY_MONTHS = 36;
export const EMPLOYMENT_STATUSES = [
  { key: 'EMPLOYED', label: 'Employed' }, { key: 'SELF_EMPLOYED', label: 'Self-employed' }, { key: 'STUDENT', label: 'Student' },
  { key: 'RETIRED', label: 'Retired' }, { key: 'BENEFITS', label: 'On benefits' }, { key: 'OTHER', label: 'Other' },
] as const;
/** A viewing is a one-hour slot on a weekday, 09:00–17:00 (leasing/schemas.ts VIEWING_HOURS). */
export const VIEWING_HOURS = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'] as const;
/** "10:00–11:00". A slot saved before hours existed (morning / afternoon / evening) reads as that word. */
export const slotLabel = (time?: string | null, part?: string | null) =>
  time && /^\d{2}:\d{2}$/.test(time) ? `${time}–${String(Number(time.slice(0, 2)) + 1).padStart(2, '0')}:00` : (part ?? time ?? '');
export const isWeekday = (d: string) => { const n = new Date(`${d}T12:00:00Z`).getUTCDay(); return n >= 1 && n <= 5; };
/** The first weekday on or after today + n days (Europe/London) — the default viewing day is tomorrow, or Monday. */
export function nextWeekday(fromDaysAhead = 1) {
  const d = new Date(`${todayIso()}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + fromDaysAhead);
  while (d.getUTCDay() === 0 || d.getUTCDay() === 6) d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
/** The most a website visitor may go under the advert, for a viewing budget or an offered rent (public/enquiries.service.ts). */
export const MAX_UNDER_ADVERT_PERCENT = 5;
export const minUnderAdvertMinor = (advertMinor: number) => Math.ceil((advertMinor * (100 - MAX_UNDER_ADVERT_PERCENT)) / 100);

export interface AddressLine { address: string; years: string; months: string }
export interface Member { name: string; dateOfBirth: string; relationship: string; adult: boolean }
export interface ApplicationState {
  /** First name and last name, two fields (2 Nov 2026); the server derives aboutYou.fullName from them. */
  title: string; firstName: string; lastName: string; dateOfBirth: string; email: string; mobile: string;
  current: AddressLine; previous: AddressLine[];
  members: Member[]; pets: boolean; petsDetail: string; smoker: boolean;
  moveInDate: string; offeredRent: string;
  /** Parking and furniture (an extra cost) — asked on every form; kept on the reservation and in the agreement. */
  parkingRequired: boolean; furnitureRequired: boolean;
  /** Why they offer less than the advert (optional; Lotus Living reads it with the rent request). */
  offerReason?: string;
  employmentStatus: string; employer: string; jobTitle: string; startDate: string;
  annualIncome: string; otherIncome: string; benefits: string;
  guarantor: boolean; guarantorName: string; guarantorEmail: string;
  firstTimeRenter: boolean; landlordName: string; landlordEmail: string; landlordPhone: string; landlordHowLong: string; arrears: boolean;
  nationality: string; shareCode: string;
  privacy: boolean; referencing: boolean; declaration: boolean; signatureName: string;
  /** When suits them for a viewing (mode 'viewing'): 1–3 weekday one-hour slots. Empty for an application. */
  preferredSlots: Array<{ date: string; time: string }>;
  /**
   * The first day the flat can be moved into ('YYYY-MM-DD') when that is after today — the API's `availableFrom`
   * (unitAvailability on the server). The move-in picker's min; never sent. Set with withFreeFrom.
   */
  freeFrom?: string | null;
  /** `freeFrom` is the day the current tenant leaves (they have given notice). */
  freeNotice?: boolean;
}
export type FormMode = 'viewing' | 'apply';

export const emptyAddress = (): AddressLine => ({ address: '', years: '', months: '' });
export function emptyApplication(seed: Partial<ApplicationState> = {}): ApplicationState {
  return {
    title: '', firstName: '', lastName: '', dateOfBirth: '', email: '', mobile: '', current: emptyAddress(), previous: [],
    members: [], pets: false, petsDetail: '', smoker: false, moveInDate: firstOfNextMonth(), offeredRent: '', parkingRequired: false, furnitureRequired: false,
    employmentStatus: 'EMPLOYED', employer: '', jobTitle: '', startDate: '', annualIncome: '', otherIncome: '', benefits: '',
    guarantor: false, guarantorName: '', guarantorEmail: '',
    firstTimeRenter: true, landlordName: '', landlordEmail: '', landlordPhone: '', landlordHowLong: '', arrears: false,
    nationality: '', shareCode: '', privacy: false, referencing: false, declaration: false, signatureName: '', preferredSlots: [],
    ...seed,
  };
}

export const todayIso = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(new Date());
/** The first day of next month (Europe/London) — the move-in date every form starts with. */
export function firstOfNextMonth() {
  const [y, m] = todayIso().split('-').map(Number) as [number, number];
  return `${m === 12 ? y + 1 : y}-${String((m % 12) + 1).padStart(2, '0')}-01`;
}
/** "16 Dec 2026". */
export const longDay = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
/** The first move-in day the picker allows: the flat's free date, or today when it is free now. */
export const earliestMoveIn = (s: Pick<ApplicationState, 'freeFrom'>) => (s.freeFrom && s.freeFrom > todayIso() ? s.freeFrom : todayIso());
/**
 * The flat's free date (the API's `availableFrom`) onto the form: kept as the picker's min, and a move-in before it moves
 * up to it. `reset`: a new form — the move-in starts at the free date (today when the flat is free now).
 */
export function withFreeFrom(s: ApplicationState, availableFrom: string | null | undefined, opts: { notice?: boolean; reset?: boolean } = {}): ApplicationState {
  const today = todayIso();
  const freeFrom = availableFrom && availableFrom > today ? availableFrom : null;
  const earliest = freeFrom ?? today;
  const moveInDate = opts.reset || !s.moveInDate || s.moveInDate < earliest ? earliest : s.moveInDate;
  return { ...s, freeFrom, freeNotice: !!freeFrom && !!opts.notice, moveInDate };
}
/** The grey line under a move-in picker while the flat is not free yet: "Free from 16 Dec 2026 (current tenant leaving)". */
export function freeFromLine(s: Pick<ApplicationState, 'freeFrom' | 'freeNotice'>, word: 'Free' | 'Available' = 'Free') {
  if (!s.freeFrom || s.freeFrom <= todayIso()) return null;
  return `${word} from ${longDay(s.freeFrom)}${s.freeNotice ? ' (current tenant leaving)' : ''}`;
}
/** The move-in date's problem, if any (every mode): missing, past, or before the flat is free. */
function moveInError(s: ApplicationState): string | null {
  if (!s.moveInDate) return 'Pick your move-in date';
  if (s.moveInDate < todayIso()) return 'Pick today or a later date';
  if (s.moveInDate < earliestMoveIn(s)) return `The flat is available from ${longDay(earliestMoveIn(s))} — choose a move-in on or after that`;
  return null;
}
/** Only the move-in rule — for forms that check nothing else before sending (the staff short path). */
export const moveInErrors = (s: ApplicationState): Record<string, string> => { const m = moveInError(s); return m ? { moveInDate: m } : {}; };
/**
 * The server still refused the move-in (409 MOVE_IN_TOO_EARLY — the page was open while the flat's date moved): the date
 * it gave, onto the form, so the move-in field says so under it. Null for any other error.
 */
export function moveInTooEarly(err: unknown): { availableFrom: string; message: string } | null {
  const e = err as { code?: string; message?: string; details?: { availableFrom?: unknown } } | null;
  if (!e || e.code !== 'MOVE_IN_TOO_EARLY') return null;
  const d = e.details?.availableFrom;
  return typeof d === 'string' ? { availableFrom: d, message: e.message ?? '' } : null;
}
const pence = (pounds: string) => { const n = Number(String(pounds).replace(/[£,\s]/g, '')); return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null; };
const monthsOf = (a: AddressLine) => (Number(a.years) || 0) * 12 + (Number(a.months) || 0);
export const monthsCovered = (s: ApplicationState) => monthsOf(s.current) + s.previous.reduce((n, p) => n + monthsOf(p), 0);
export const isAdult = (dob: string) => !!dob && dob <= `${Number(todayIso().slice(0, 4)) - 18}${todayIso().slice(4)}`;
/** A valid phone number for its country (the field has a country dropdown; the value is E.164) — lib/phone.ts. */
export const digitsOnly = (v: string) => phoneOk(v);

/** Live affordability: total income ÷ 30 against the rent offered. */
export function affordability(s: ApplicationState, rentMinor: number) {
  const income = (pence(s.annualIncome) ?? 0) + (pence(s.otherIncome) ?? 0) + (pence(s.benefits) ?? 0);
  const max = Math.floor(income / AFFORDABILITY_MULTIPLE);
  return { incomeMinor: income, maxRentMinor: max, affordable: income > 0 && max >= rentMinor };
}

export const EMAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const pounds = (minor: number) => `£${(minor / 100).toLocaleString('en-GB')}`;

/**
 * Every problem with the applicant's own form, by field — the website shows each one under its box as it is filled in
 * (and the lot on a submit attempt). Keys are the field names; `member.N` is the Nth extra person; `consent` the three
 * ticks. Empty = the form may go.
 */
export function fieldErrors(s: ApplicationState, advertMinor: number, mode: FormMode = 'apply'): Record<string, string> {
  if (mode === 'viewing') return viewingErrors(s);
  const e: Record<string, string> = {};
  const today = todayIso();
  Object.assign(e, nameErrors(s));
  if (!s.dateOfBirth) e.dateOfBirth = 'Enter your date of birth';
  else if (s.dateOfBirth > today) e.dateOfBirth = 'That date is in the future';
  else if (!isAdult(s.dateOfBirth)) e.dateOfBirth = 'You need to be 18 or over to rent';
  if (!EMAIL_OK.test(s.email.trim())) e.email = 'Enter a valid email, like name@example.com';
  if (!digitsOnly(s.mobile)) e.mobile = phoneError(s.mobile);
  if (s.current.address.trim().length < 3) e.currentAddress = 'Enter your current address';
  if (monthsCovered(s) < ADDRESS_HISTORY_MONTHS) e.addressHistory = 'Your addresses need to cover the last three years';
  s.members.forEach((m, i) => {
    if (!m.name.trim()) e[`member.${i}`] = 'Enter their name';
    else if (m.dateOfBirth && m.dateOfBirth > today) e[`member.${i}`] = 'That date is in the future';
  });
  const mv = moveInError(s); if (mv) e.moveInDate = mv;
  const offered = pence(s.offeredRent);
  if (!offered) e.offeredRent = 'Enter the rent you offer';
  else if (advertMinor && offered > advertMinor) e.offeredRent = `Not above the advertised ${pounds(advertMinor)}`;
  else if (advertMinor && offered < minUnderAdvertMinor(advertMinor)) e.offeredRent = `At least ${pounds(minUnderAdvertMinor(advertMinor))} a month`;
  if (pence(s.annualIncome) === null && s.employmentStatus !== 'STUDENT' && s.employmentStatus !== 'RETIRED') e.annualIncome = 'Enter your annual income';
  if (s.startDate && s.startDate > today) e.startDate = 'That date is in the future';
  if (s.guarantor) {
    if (s.guarantorName.trim().length < 2) e.guarantorName = "Enter your guarantor's name";
    if (!EMAIL_OK.test(s.guarantorEmail.trim())) e.guarantorEmail = 'Enter a valid email';
  }
  if (!s.firstTimeRenter && s.landlordEmail.trim() && !EMAIL_OK.test(s.landlordEmail.trim())) e.landlordEmail = 'Enter a valid email';
  if (!s.firstTimeRenter && s.landlordPhone.trim() && !digitsOnly(s.landlordPhone)) e.landlordPhone = phoneError(s.landlordPhone);
  // Right to Rent (applicant.schemas.ts): a nationality from the list (lib/nationalities.ts), and a share code unless British or Irish.
  if (!s.nationality.trim()) e.nationality = 'Enter your nationality';
  else if (!findNationality(s.nationality)) e.nationality = 'Pick your nationality from the list';
  else if (needsShareCode(s.nationality) && !s.shareCode.trim()) e.shareCode = 'Enter your Right to Rent share code (needed unless you are British or Irish)';
  if (!s.privacy || !s.referencing || !s.declaration) e.consent = 'Tick all three boxes';
  return e;
}
/** First name and last name, both required (keys `firstName` / `lastName`). */
function nameErrors(s: ApplicationState): Record<string, string> {
  const e: Record<string, string> = {};
  if (!s.firstName.trim()) e.firstName = 'Enter your first name';
  if (!s.lastName.trim()) e.lastName = 'Enter your last name';
  return e;
}
/** "First Last" — the name the form holds, as one line (the declaration, a thank-you, the signature's default). */
export const nameOf = (s: Pick<ApplicationState, 'firstName' | 'lastName'>) => [s.firstName.trim(), s.lastName.trim()].filter(Boolean).join(' ');
/** A full name as first and last, split on the last space ("Mary Ann Smith" → Mary Ann / Smith) — older forms and accounts. */
export function splitName(full: string | null | undefined): { firstName: string; lastName: string } {
  const t = String(full ?? '').trim().replace(/\s+/g, ' ');
  const i = t.lastIndexOf(' ');
  return i < 0 ? { firstName: t, lastName: '' } : { firstName: t.slice(0, i), lastName: t.slice(i + 1) };
}
/** A stored aboutYou (or a lead / account) as first and last: its own when it has them, else its full name split. */
function namesFrom(first: unknown, last: unknown, full: unknown, seed: Partial<ApplicationState> = {}) {
  const n = first || last ? { firstName: str(first), lastName: str(last) } : splitName(str(full));
  return n.firstName || n.lastName ? n : { firstName: seed.firstName ?? '', lastName: seed.lastName ?? '' };
}

/**
 * Mode 'viewing': only who they are, how to reach them, the move-in date, when suits them and the privacy tick. Keys as
 * fieldErrors; `preferredSlots` for the slot picker, `consent` for the tick.
 */
function viewingErrors(s: ApplicationState): Record<string, string> {
  const e: Record<string, string> = {};
  Object.assign(e, nameErrors(s));
  if (!EMAIL_OK.test(s.email.trim())) e.email = 'Enter a valid email, like name@example.com';
  if (!digitsOnly(s.mobile)) e.mobile = phoneError(s.mobile);
  const mv = moveInError(s); if (mv) e.moveInDate = mv;
  Object.assign(e, slotErrors(s));
  if (!s.privacy) e.consent = 'Tick that we may contact you about this home';
  return e;
}
/** The viewing slots: 1–3 weekday one-hour slots, today or later (key `preferredSlots`). */
export function slotErrors(s: ApplicationState): Record<string, string> {
  const today = todayIso();
  const slots = s.preferredSlots ?? [];
  if (!slots.length) return { preferredSlots: 'Pick at least one time that suits you' };
  if (slots.length > 3) return { preferredSlots: 'Pick up to three times' };
  if (slots.some((x) => !/^\d{4}-\d{2}-\d{2}$/.test(x.date) || x.date < today)) return { preferredSlots: 'Pick today or a later date' };
  if (slots.some((x) => !isWeekday(x.date))) return { preferredSlots: 'Viewings are on weekdays only' };
  if (slots.some((x) => !(VIEWING_HOURS as readonly string[]).includes(x.time))) return { preferredSlots: 'Pick a one-hour slot between 09:00 and 17:00' };
  return {};
}

/**
 * The applicant answering an "Ask to refill" (their page /apply/<token>, /app/my-booking): the form is saved with the staff
 * rules (applicantFormSaveSchema), so only what Lotus Living asked for (`asked`, the refill's field keys — the same keys as
 * fieldErrors, plus the section words previousAddresses / members / guarantor) is required, with the name, a way to reach
 * them and the move-in date, and anything typed has to be right (an email that is an email, a date of birth of 18+).
 */
export function refillErrors(s: ApplicationState, advertMinor: number, asked: string[]): Record<string, string> {
  const all = fieldErrors(s, advertMinor, 'apply');
  const want = new Set(['firstName', 'lastName', 'moveInDate', ...asked]);
  if (want.has('previousAddresses')) want.add('addressHistory');
  if (want.has('guarantor') || s.guarantor) { want.add('guarantorName'); want.add('guarantorEmail'); }
  // Typed in, so it has to be valid whether or not it was asked for.
  if (s.dateOfBirth) want.add('dateOfBirth');
  if (s.email.trim()) want.add('email');
  if (s.mobile.trim()) want.add('mobile');
  if (s.offeredRent.trim()) want.add('offeredRent');
  if (s.startDate) want.add('startDate');
  want.add('landlordEmail'); want.add('landlordPhone');
  if (want.has('nationality') || want.has('shareCode')) { want.add('nationality'); want.add('shareCode'); }
  const e: Record<string, string> = {};
  for (const [k, v] of Object.entries(all)) {
    if (want.has(k) || (k.startsWith('member.') && (want.has('members') || s.members.length > 0))) e[k] = v;
  }
  if (!s.email.trim() && !s.mobile.trim()) e.email = 'Give an email or a mobile';
  // An asked-for field the full rules do not require (an employer, a share code…) still has to be filled in.
  const text: Record<string, string> = {
    title: s.title, employer: s.employer, jobTitle: s.jobTitle, startDate: s.startDate, otherIncome: s.otherIncome, benefits: s.benefits,
    shareCode: s.shareCode, nationality: s.nationality, pets: s.pets ? s.petsDetail : 'n/a',
    ...(s.firstTimeRenter ? {} : { landlordName: s.landlordName, landlordEmail: s.landlordEmail, landlordPhone: s.landlordPhone }),
  };
  for (const k of asked) {
    const key = k === 'pets' ? 'petsDetail' : k;
    if (k in text && !String(text[k]).trim() && !e[key]) e[key] = 'Lotus Living asked for this — please fill it in';
  }
  if (asked.includes('previousAddresses') && !s.previous.some((p) => p.address.trim()) && !e.addressHistory) e.addressHistory = 'Add your previous address';
  return e;
}
/** Is this field one the refill asks for (shown with its star)? */
export function refillAsks(asked: string[], key: string) {
  if (asked.includes(key)) return true;
  if (key === 'addressHistory') return asked.includes('previousAddresses');
  if (key.startsWith('member.')) return asked.includes('members');
  if (key === 'guarantorName' || key === 'guarantorEmail') return asked.includes('guarantor');
  return key === 'firstName' || key === 'lastName' || key === 'moveInDate';
}

/** The same problems as one list (a submit attempt). */
export const formErrors = (s: ApplicationState, advertMinor: number, mode: FormMode = 'apply') => Object.values(fieldErrors(s, advertMinor, mode));

const opt = (v: string) => (v.trim() ? v.trim() : null);
const address = (a: AddressLine) => ({ address: a.address.trim(), months: monthsOf(a) });

/**
 * The body's `form` for the API. `staff`: consents only when ticked (the server records who took them). Mode 'viewing'
 * (a viewing request): the optional sections are sent only when something is in them, consents only when ticked, and
 * `preferredSlots` goes with the form. `complete` (the website's "Request a viewing", applicant.schemas.ts
 * websiteViewingFormSchema): the whole application exactly as mode 'apply' sends it, plus the slots.
 */
export function toPayload(s: ApplicationState, opts: { staff?: boolean; mode?: FormMode; complete?: boolean } = {}) {
  const slots = () => (s.preferredSlots ?? []).map((x) => ({ date: x.date, time: x.time }));
  if (opts.mode === 'viewing' && opts.complete) return { ...basePayload(s, {}), preferredSlots: slots() };
  if (opts.mode !== 'viewing') return { ...basePayload(s, opts), preferredSlots: undefined as Array<{ date: string; time: string }> | undefined };
  // The staff shape already sends the optional sections only when filled and the consents only when ticked.
  return { ...basePayload(s, { staff: true }), preferredSlots: slots() };
}

function basePayload(s: ApplicationState, opts: { staff?: boolean }) {
  const now = new Date().toISOString();
  const stamp = (b: boolean) => (b ? now : undefined);
  return {
    aboutYou: {
      title: opt(s.title), firstName: s.firstName.trim(), lastName: s.lastName.trim(), dateOfBirth: s.dateOfBirth || undefined, email: s.email.trim() || (opts.staff ? null : ''),
      mobile: s.mobile.trim() || (opts.staff ? null : ''), currentAddress: s.current.address.trim() ? address(s.current) : undefined,
      previousAddresses: s.previous.filter((p) => p.address.trim()).map(address),
    },
    household: {
      members: s.members.filter((m) => m.name.trim()).map((m) => ({ name: m.name.trim(), dateOfBirth: m.dateOfBirth || null, relationship: opt(m.relationship), adult: m.adult })),
      pets: { has: s.pets, detail: s.pets ? opt(s.petsDetail) : null }, smoker: s.smoker,
    },
    move: { moveInDate: s.moveInDate, offeredRentMinor: pence(s.offeredRent), offerReason: s.offerReason?.trim() || null, parkingRequired: s.parkingRequired, furnitureRequired: s.furnitureRequired },
    money: (opts.staff && !s.annualIncome && !s.employer) ? null : {
      employmentStatus: s.employmentStatus, employer: opt(s.employer), jobTitle: opt(s.jobTitle), startDate: s.startDate || null,
      annualIncomeMinor: pence(s.annualIncome) ?? 0, otherIncomeMinor: pence(s.otherIncome), benefitsMinor: pence(s.benefits),
      guarantor: { has: s.guarantor, name: s.guarantor ? opt(s.guarantorName) : null, email: s.guarantor ? opt(s.guarantorEmail) : null },
    },
    previousLandlord: s.firstTimeRenter ? { firstTimeRenter: true } : (s.landlordName || s.landlordEmail ? {
      firstTimeRenter: false, name: opt(s.landlordName), email: opt(s.landlordEmail), phone: opt(s.landlordPhone), howLong: opt(s.landlordHowLong), arrears: s.arrears,
    } : null),
    rightToRent: opts.staff && !s.nationality.trim() ? null : { nationality: s.nationality.trim(), shareCode: opt(s.shareCode) },
    consent: opts.staff
      ? { privacyNoticeAt: stamp(s.privacy), referencingConsentAt: stamp(s.referencing), declarationAt: stamp(s.declaration), signatureName: opt(s.signatureName) ?? undefined }
      // The website asks for no typed signature: ticking the declaration signs it in the applicant's own name.
      : { privacyNoticeAt: now, referencingConsentAt: now, declarationAt: now, signatureName: s.signatureName.trim() || nameOf(s) },
  };
}

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const poundsText = (minor: unknown) => (typeof minor === 'number' && minor > 0 ? String(minor / 100) : '');
const addressLine = (a: unknown): AddressLine => {
  const o = (a ?? {}) as { address?: string; months?: number };
  const m = Number(o.months ?? 0) || 0;
  return { address: str(o.address), years: String(Math.floor(m / 12)), months: String(m % 12) };
};

/**
 * The API's form JSON (reservations.application, leads.application — the payload toPayload makes, plus whatever the server
 * added) back onto the state. toPayload(fromApplication(p)) gives p again (consent times aside; no previousLandlord comes
 * back as first-time renting — the box unticked).
 */
export function fromApplication(json: Record<string, any> | null | undefined, seed: Partial<ApplicationState> = {}): ApplicationState {
  const a = (json ?? {}) as Record<string, any>;
  const you = a.aboutYou ?? {}; const hh = a.household ?? {}; const mv = a.move ?? {}; const mo = a.money ?? null;
  const pl = a.previousLandlord ?? null; const rtr = a.rightToRent ?? null; const c = a.consent ?? {};
  const g = mo?.guarantor ?? null;
  return emptyApplication({
    ...seed,
    title: str(you.title), ...namesFrom(you.firstName, you.lastName, you.fullName, seed), dateOfBirth: str(you.dateOfBirth), email: str(you.email), mobile: str(you.mobile),
    current: you.currentAddress ? addressLine(you.currentAddress) : emptyAddress(),
    previous: Array.isArray(you.previousAddresses) ? you.previousAddresses.map(addressLine) : [],
    members: Array.isArray(hh.members) ? hh.members.map((m: Record<string, any>) => ({ name: str(m.name), dateOfBirth: str(m.dateOfBirth), relationship: str(m.relationship), adult: m.adult !== false })) : [],
    pets: !!hh.pets?.has, petsDetail: str(hh.pets?.detail), smoker: !!hh.smoker,
    moveInDate: str(mv.moveInDate) || seed.moveInDate || firstOfNextMonth(), offeredRent: poundsText(mv.offeredRentMinor), offerReason: str(mv.offerReason),
    parkingRequired: !!mv.parkingRequired, furnitureRequired: !!mv.furnitureRequired,
    employmentStatus: str(mo?.employmentStatus) || 'EMPLOYED', employer: str(mo?.employer), jobTitle: str(mo?.jobTitle), startDate: str(mo?.startDate),
    annualIncome: poundsText(mo?.annualIncomeMinor), otherIncome: poundsText(mo?.otherIncomeMinor), benefits: poundsText(mo?.benefitsMinor),
    guarantor: !!g?.has, guarantorName: str(g?.name), guarantorEmail: str(g?.email),
    // No previous landlord on the form (a viewing request, a contact-only save): "rented before" starts unticked, as on
    // a new form (3 Nov 2026) — ticked only when they said so and gave the landlord.
    firstTimeRenter: pl ? !!pl.firstTimeRenter : true, landlordName: str(pl?.name), landlordEmail: str(pl?.email), landlordPhone: str(pl?.phone), landlordHowLong: str(pl?.howLong), arrears: !!pl?.arrears,
    nationality: findNationality(str(rtr?.nationality))?.nationality ?? str(rtr?.nationality), shareCode: str(rtr?.shareCode),
    privacy: !!c.privacyNoticeAt, referencing: !!c.referencingConsentAt, declaration: !!c.declarationAt, signatureName: str(c.signatureName),
    preferredSlots: Array.isArray(a.preferredSlots) ? a.preferredSlots.map((x: Record<string, any>) => ({ date: str(x.date), time: str(x.time) })) : (seed.preferredSlots ?? []),
  });
}

/** A lead onto the form (staff "Convert to application"): its viewing request's form when it has one, else its fields. */
export function fromLead(l: Record<string, any>): ApplicationState {
  const r = (l.requirements ?? {}) as Record<string, any>;
  const moveInDate = r.moveIn && r.moveIn >= todayIso() ? r.moveIn : (l.moveInFrom && l.moveInFrom >= todayIso() ? l.moveInFrom : firstOfNextMonth());
  if (l.application && typeof l.application === 'object') {
    const s = fromApplication(l.application, { preferredSlots: Array.isArray(r.preferredSlots) ? r.preferredSlots : [] });
    // A move-in date that has passed since the request is moved on to the default, as for a lead without a form.
    return s.moveInDate < todayIso() ? { ...s, moveInDate } : s;
  }
  return emptyApplication({
    ...namesFrom(l.firstName, l.lastName, l.name), email: l.email ?? '', mobile: l.phone ?? '',
    moveInDate, pets: !!(r.pets?.has ?? l.pets), petsDetail: r.pets?.detail ?? '',
    preferredSlots: Array.isArray(r.preferredSlots) ? r.preferredSlots.filter((x: Record<string, any>) => x?.date && x?.time).map((x: Record<string, any>) => ({ date: String(x.date), time: String(x.time) })) : [],
  });
}

// ── The form in four steps (29 Oct 2026): the website's Request a viewing and Apply, and the app's form ───────────────
// The rules stay in fieldErrors / siteFormErrors; a step is the set of field keys it holds, so a step's problems are the
// form's problems filtered to that step (stepErrors) — no rule is written twice. The app keeps a copy of this block.

/** The four steps, in order — the short names the step indicator shows. */
export const FORM_STEPS = ['About you', "Where you've lived", 'Money', 'The move'] as const;
/** Which step holds each field key (fieldErrors' keys, plus the documents' `passport` / `rtrDocument`). */
const STEP_OF_FIELD: Record<string, number> = {
  firstName: 0, lastName: 0, fullName: 0, dateOfBirth: 0, email: 0, mobile: 0,
  currentAddress: 1, addressHistory: 1, nationality: 1, shareCode: 1, passport: 1, rtrDocument: 1,
  employmentStatus: 2, employer: 2, jobTitle: 2, startDate: 2, annualIncome: 2, otherIncome: 2, benefits: 2,
  guarantorName: 2, guarantorEmail: 2, landlordName: 2, landlordEmail: 2, landlordPhone: 2,
  moveInDate: 3, offeredRent: 3, preferredSlots: 3, petsDetail: 3, consent: 3,
};
/** The step (0–3) a field key sits on; `member.N` is About you; anything unknown is the last step. */
export const stepOfField = (key: string) => (key.startsWith('member.') ? 0 : STEP_OF_FIELD[key] ?? FORM_STEPS.length - 1);
/** The form's problems that sit on one step. Empty = that step may go on. */
export function stepErrors(errs: Record<string, string>, step: number): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(errs)) if (stepOfField(k) === step) out[k] = v;
  return out;
}
/** The first step with a problem, or -1 when the whole form is right. */
export const firstFailingStep = (errs: Record<string, string>) => Object.keys(errs).reduce((min, k) => (min < 0 ? stepOfField(k) : Math.min(min, stepOfField(k))), -1);

/** A server message read as the field it is about (applicant.schemas.ts wording) — most specific first. */
const MESSAGE_FIELD: Array<[RegExp, string]> = [
  [/passport/i, 'passport'], [/right to rent document/i, 'rtrDocument'], [/share code/i, 'shareCode'], [/nationalit/i, 'nationality'],
  [/guarantor/i, 'guarantorName'], [/three years|previous address/i, 'addressHistory'], [/address/i, 'currentAddress'],
  [/18 or over|date of birth/i, 'dateOfBirth'], [/first name/i, 'firstName'], [/last name/i, 'lastName'], [/full name|your name|applicant's name/i, 'firstName'], [/move-in/i, 'moveInDate'],
  [/rent you offer|advertised rent|bidding|budget/i, 'offeredRent'], [/weekday|slot|times? that suits|up to three times/i, 'preferredSlots'],
  [/tick|sign/i, 'consent'], [/email/i, 'email'], [/phone|mobile|number/i, 'mobile'],
];
const fieldOfMessage = (m: string) => MESSAGE_FIELD.find(([re]) => re.test(m))?.[1] ?? null;
/** A server issue's path (the staff routes' details: `form.aboutYou.dateOfBirth`) as the field it is about. */
const PATH_FIELD: Record<string, string> = {
  firstName: 'firstName', lastName: 'lastName', fullName: 'firstName', dateOfBirth: 'dateOfBirth', email: 'email', mobile: 'mobile', currentAddress: 'currentAddress', previousAddresses: 'addressHistory',
  nationality: 'nationality', shareCode: 'shareCode', annualIncomeMinor: 'annualIncome', money: 'annualIncome', guarantor: 'guarantorName',
  moveInDate: 'moveInDate', offeredRentMinor: 'offeredRent', consent: 'consent', preferredSlots: 'preferredSlots', rightToRent: 'nationality',
};
const fieldOfPath = (path: string) => { for (const seg of path.split('.').reverse()) if (PATH_FIELD[seg]) return PATH_FIELD[seg]!; return null; };
/**
 * A refused submit (400 VALIDATION with zod's flattened details, or a 409) as the field it is about and the message to
 * show under it — so the form can go to the step that holds it. Null when it is about no field (show it by the button).
 */
export function serverFieldError(err: unknown): { field: string; message: string } | null {
  const e = err as { code?: string; message?: string; details?: { fieldErrors?: Record<string, unknown>; formErrors?: unknown } } | null;
  if (!e || typeof e !== 'object') return null;
  if (e.code === 'MOVE_IN_TOO_EARLY') return { field: 'moveInDate', message: e.message ?? 'Pick a later move-in date' };
  if (e.code === 'EMAIL_TAKEN') return { field: 'email', message: e.message ?? 'There is already an account with that email' };
  if (e.code === 'OFFER_ABOVE_ADVERT') return { field: 'offeredRent', message: e.message ?? 'Not above the advertised rent' };
  const messages: string[] = [];
  // The staff routes (leasing/schemas.ts parseInput): [{ path, message }] — the path says which field.
  if (Array.isArray(e.details)) {
    for (const d of e.details as Array<{ path?: unknown; message?: unknown }>) {
      if (typeof d?.message !== 'string') continue;
      const field = (typeof d.path === 'string' ? fieldOfPath(d.path) : null) ?? fieldOfMessage(d.message);
      if (field) return { field, message: d.message === 'Required' ? 'This is needed' : d.message };
    }
  }
  const fe = e.details?.fieldErrors;
  if (fe && typeof fe === 'object') for (const v of Object.values(fe)) if (Array.isArray(v)) messages.push(...v.filter((x): x is string => typeof x === 'string'));
  if (Array.isArray(e.details?.formErrors)) messages.push(...(e.details!.formErrors as unknown[]).filter((x): x is string => typeof x === 'string'));
  if (e.message && e.message !== 'Validation failed') messages.push(e.message);
  for (const m of messages) { const field = fieldOfMessage(m); if (field) return { field, message: m }; }
  return null;
}
